/**
 * Starting, resuming and setting aside runs. Starting needs signal (the server hands out the run
 * id, and for anonymous runs the run token, exactly once); everything after that works offline.
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useProceduresServices,
  type ProcedurePublicRunStartRequest,
  type ProcedureRunStartView,
} from '@actschurch/ccms-sdk';
import { getDeviceId } from './device';
import { proceduresKeys, type PortalMode } from './queryKeys';
import {
  getRunRecord,
  markRunFinished,
  saveRunRecord,
  saveRunSnapshot,
  setInProgress,
  type LocalRunRecord,
} from './runStore';
import { enqueueFinish } from './syncQueue';
import { stampToDate } from './format';

function persistStart(
  code: string,
  res: ProcedureRunStartView,
  mode: PortalMode,
  title: string,
  userId: string | null,
): number {
  const runId = res.run.runID;
  saveRunRecord({
    runId,
    code,
    mode,
    token: mode === 'public' ? res.runToken ?? null : null,
    userId: mode === 'member' ? userId : null,
    title,
    runnerName: res.run.runnerName,
    startedAtMs: Date.now(),
    state: 'active',
  });
  setInProgress(code, runId);
  saveRunSnapshot(res.run);
  return runId;
}

export function useRunStarter(code: string) {
  const services = useProceduresServices();
  const queryClient = useQueryClient();

  const startMember = useCallback(
    async (title: string, userId: string | null): Promise<number> => {
      const res = await services.proceduresMember.startMemberRun(code, { deviceId: getDeviceId() });
      const runId = persistStart(code, res, 'member', title, userId);
      queryClient.setQueryData(proceduresKeys.run(runId), res.run);
      return runId;
    },
    [services, code, queryClient],
  );

  const startPublic = useCallback(
    async (title: string, request: ProcedurePublicRunStartRequest): Promise<number> => {
      const res = await services.publicProcedures.startPublicRun(code, request);
      if (!res.runToken) throw new Error('The server did not return a run token. Please try again.');
      const runId = persistStart(code, res, 'public', title, null);
      queryClient.setQueryData(proceduresKeys.run(runId), res.run);
      return runId;
    },
    [services, code, queryClient],
  );

  return { startMember, startPublic };
}

/**
 * A signed-in person's unfinished run that the API reported (portal view resumeRunID, My runs) but
 * this phone has never seen: remember it so the runner can open it (and later work offline).
 */
export function ensureMemberRecord(args: {
  runId: number;
  code: string;
  title?: string | null;
  userId: string | null;
  startedAt?: number | null;
  active?: boolean;
}): LocalRunRecord {
  const existing = getRunRecord(args.runId);
  if (existing) return existing;
  const record: LocalRunRecord = {
    runId: args.runId,
    code: args.code,
    mode: 'member',
    userId: args.userId,
    title: args.title ?? null,
    startedAtMs: stampToDate(args.startedAt ?? null)?.getTime() ?? Date.now(),
    state: args.active === false ? 'completed' : 'active',
  };
  saveRunRecord(record);
  if (record.state === 'active') setInProgress(args.code, args.runId);
  return record;
}

/** "Start again": set the old run aside (abandon is queued, so this works offline too). */
export function setRunAside(record: LocalRunRecord): void {
  enqueueFinish({ runId: record.runId, code: record.code, mode: record.mode, kind: 'abandon', request: { note: 'Started again on this phone.' } });
  markRunFinished(record.runId, 'abandoned');
}
